import { formatReg, splitRegsInText } from '@aroo/shared';
import { Fragment } from 'react';
import type { PlateSize } from './plateValue';
import { UkPlate } from './UkPlate';

interface RegTextProps {
  /** Free text that may mention regs, e.g. a stored notification title. */
  text: string;
  size?: PlateSize;
}

/**
 * Free text with any UK registrations in it drawn as inline plates. For sentences we can't
 * restructure (notification titles stored as text); structured payloads pass `reg` separately
 * and render <UkPlate> directly instead.
 */
export function RegText({ text, size = 'inline' }: RegTextProps) {
  const parts = splitRegsInText(text);
  if (parts.length === 1 && 'text' in parts[0]) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{'reg' in p ? <UkPlate reg={p.reg} size={size} /> : p.text}</Fragment>
      ))}
    </>
  );
}

interface WithPlateProps {
  /** Sentence that contains `reg`, e.g. "AB12 CDE: MOT due in 5 days". */
  text: string;
  /** The reg the payload carries alongside the text (a structured field, not parsed). */
  reg: string | null | undefined;
  size?: PlateSize;
}

/**
 * Text from a structured payload whose `reg` field also appears in its wording: the first
 * occurrence (raw or DVLA-spaced) becomes an inline plate. Without one, falls back to <RegText>.
 */
export function WithPlate({ text, reg, size = 'inline' }: WithPlateProps) {
  if (!reg) return <RegText text={text} size={size} />;
  const candidates = [formatReg(reg), reg.trim()].filter((c) => c.length > 0);
  for (const c of candidates) {
    const at = text.indexOf(c);
    if (at < 0) continue;
    return (
      <>
        {text.slice(0, at)}
        <UkPlate reg={reg} size={size} />
        {text.slice(at + c.length)}
      </>
    );
  }
  return <RegText text={text} size={size} />;
}
